import { create } from 'zustand';

import type { BindingState, OAuthProvider } from '@/lib/api/auth';

interface PendingOAuthBinding {
    provider: OAuthProvider;
    accountHash: string;
    /** Owner just unlinked from this identity and must not be auto-selected. */
    excludedOwner?: string;
}

interface OAuthBindingDialogState {
    pending: PendingOAuthBinding | null;
    protect: (accountHash: string, excludedOwner: string) => void;
    release: (accountHash: string) => void;
    request: (binding: PendingOAuthBinding) => void;
    /** Close the dialog without forgetting a switch safety guard. */
    clear: () => void;
    /** Finish a successful binding and clear its old-owner safety guard. */
    complete: () => void;
}

const EXCLUDED_OWNER_STORAGE_KEY = 'snappy.oauth.excluded-owner.v1';
interface ProtectedOAuthBinding {
    accountHash: string;
    owner: string;
    /**
     * When the guard was written. The unlink transaction is still unconfirmed
     * at that moment, so a freshly written guard outranks anything the server
     * says about the binding. Null for guards persisted before this field
     * existed, which are treated as old rather than fresh.
     */
    protectedAtMs?: number;
}

/** What a guard protects, without the account hash that keys it. */
export interface ProtectedOAuthBindingRecord {
    owner: string;
    protectedAtMs: number | null;
}

const MAX_PROTECTED_BINDINGS = 32;
let inMemoryProtections = new Map<string, ProtectedOAuthBindingRecord>();
let lastPersistedValue: string | null = null;

export function shouldRequestOAuthBinding(input: {
    provider?: OAuthProvider | null;
    accountHash?: string | null;
    bindingState?: BindingState | null;
}): input is {
    provider: OAuthProvider;
    accountHash: string;
    bindingState: 'unbound';
} {
    return (
        !!input.provider &&
        !!input.accountHash &&
        input.bindingState === 'unbound'
    );
}

function normalizeAccountHash(accountHash: string): string {
    return accountHash.toLowerCase();
}

function parseProtections(
    raw: string,
): Map<string, ProtectedOAuthBindingRecord> | null {
    const parsed = JSON.parse(raw) as {
        accountHash?: unknown;
        owner?: unknown;
        protections?: unknown;
    };
    const entries: ProtectedOAuthBinding[] = Array.isArray(parsed.protections)
        ? parsed.protections.filter(
            (entry): entry is ProtectedOAuthBinding =>
                !!entry &&
                  typeof entry === 'object' &&
                  typeof (entry as ProtectedOAuthBinding).accountHash ===
                      'string' &&
                  typeof (entry as ProtectedOAuthBinding).owner === 'string',
        )
        : typeof parsed.accountHash === 'string' &&
            typeof parsed.owner === 'string'
            ? [{ accountHash: parsed.accountHash, owner: parsed.owner }]
            : [];
    if (entries.length === 0) return null;
    return new Map(
        entries
            .slice(-MAX_PROTECTED_BINDINGS)
            .map(({ accountHash, owner, protectedAtMs }) => [
                normalizeAccountHash(accountHash),
                {
                    owner,
                    protectedAtMs:
                        typeof protectedAtMs === 'number' &&
                        Number.isFinite(protectedAtMs)
                            ? protectedAtMs
                            : null,
                },
            ]),
    );
}

function readProtections(): Map<string, ProtectedOAuthBindingRecord> {
    try {
        const raw = localStorage.getItem(EXCLUDED_OWNER_STORAGE_KEY);
        if (!raw) {
            if (lastPersistedValue !== null) {
                inMemoryProtections = new Map();
            }
            lastPersistedValue = null;
            return inMemoryProtections;
        }
        if (raw !== lastPersistedValue) {
            const parsed = parseProtections(raw);
            if (parsed) inMemoryProtections = parsed;
            lastPersistedValue = raw;
        }
        return inMemoryProtections;
    } catch {
        return inMemoryProtections;
    }
}

function readExcludedOwner(accountHash: string): string | undefined {
    return readProtections().get(normalizeAccountHash(accountHash))?.owner;
}

export function hasProtectedOAuthBinding(accountHash?: string): boolean {
    const protections = readProtections();
    return accountHash
        ? protections.has(normalizeAccountHash(accountHash))
        : protections.size > 0;
}

/**
 * The guard held for one identity, including when it was written. Callers that
 * have to decide whether a guard still outranks the server's view of the
 * binding need the age, not just the existence.
 */
export function getProtectedOAuthBinding(
    accountHash?: string,
): ProtectedOAuthBindingRecord | null {
    if (!accountHash) return null;
    return readProtections().get(normalizeAccountHash(accountHash)) ?? null;
}

function persistProtections(
    protections: Map<string, ProtectedOAuthBindingRecord>,
): void {
    inMemoryProtections = protections;
    if (protections.size === 0) {
        try {
            localStorage.removeItem(EXCLUDED_OWNER_STORAGE_KEY);
            lastPersistedValue = null;
        } catch {
            // The in-memory state remains authoritative for this tab.
        }
        return;
    }
    const value = JSON.stringify({
        protections: [...protections].map(
            ([accountHash, { owner, protectedAtMs }]) => ({
                accountHash,
                owner,
                ...(protectedAtMs === null ? {} : { protectedAtMs }),
            }),
        ),
    });
    try {
        localStorage.setItem(EXCLUDED_OWNER_STORAGE_KEY, value);
        lastPersistedValue = value;
    } catch {
        // The in-memory guards remain active when storage is unavailable.
    }
}

function saveExcludedOwner(accountHash: string, owner: string): void {
    const protections = new Map(readProtections());
    protections.delete(normalizeAccountHash(accountHash));
    protections.set(normalizeAccountHash(accountHash), {
        owner,
        protectedAtMs: Date.now(),
    });
    while (protections.size > MAX_PROTECTED_BINDINGS) {
        const oldest = protections.keys().next().value;
        if (oldest === undefined) break;
        protections.delete(oldest);
    }
    persistProtections(protections);
}

function clearExcludedOwner(accountHash: string): void {
    const protections = new Map(readProtections());
    protections.delete(normalizeAccountHash(accountHash));
    persistProtections(protections);
}

export const useOAuthBindingDialogStore = create<OAuthBindingDialogState>(
    (set, get) => ({
        pending: null,
        protect: (accountHash, excludedOwner) =>
            saveExcludedOwner(accountHash, excludedOwner),
        release: (accountHash) => clearExcludedOwner(accountHash),
        request: (binding) => {
            const excludedOwner =
                binding.excludedOwner ?? readExcludedOwner(binding.accountHash);
            if (binding.excludedOwner) {
                saveExcludedOwner(binding.accountHash, binding.excludedOwner);
            }
            set({
                pending: excludedOwner
                    ? { ...binding, excludedOwner }
                    : binding,
            });
        },
        clear: () => set({ pending: null }),
        complete: () => {
            const accountHash = get().pending?.accountHash;
            if (accountHash) clearExcludedOwner(accountHash);
            set({ pending: null });
        },
    }),
);
